import type {
  ChaveDeAcesso,
  Commentary,
  EssayKind,
  EstadoDoComentario,
  EstadoDoItem,
  ExamModule,
  ExamStatus,
  OrderStatus,
  QuestionStatus,
  QuestionSummary,
} from '@titularis/api-client';

/**
 * Vocabulário do domínio em português, num lugar só.
 *
 * O código, as rotas e o banco falam inglês (G-CANONICAL); a interface fala a
 * língua do Róger e do Calebe. É aqui que a tradução acontece — não espalhada
 * em cada tela, onde "Questões Objetivas" viraria "Objetivas" numa e
 * "Objetiva" na outra.
 */
export const MODULO_LABEL: Record<ExamModule, string> = {
  enac: 'ENAC',
  objective: 'Questões Objetivas',
  essay: 'Questões Discursivas',
};

export const MODULOS = Object.keys(MODULO_LABEL) as ExamModule[];

/**
 * Os estados da régua do comentário (D-22).
 *
 * O rótulo diz **o que falta**, e não o nome interno do estado: quem olha o
 * painel está decidindo o que fazer a seguir, e "Falta a aba" responde isso
 * enquanto "falta_aba" só descreve.
 */
export const ESTADO_COMENTARIO_LABEL: Record<EstadoDoComentario, string> = {
  comentada: 'Comentada',
  falta_aba: 'Falta a aba',
  faltam_itens: 'Faltam itens',
  em_andamento: 'Em andamento',
  so_modelo: 'Só o modelo',
  so_comentario: 'Só o comentário',
  sem_comentario: 'Não começada',
};

export const ESTADO_COMENTARIO_COR: Record<EstadoDoComentario, string> = {
  comentada: 'green',
  falta_aba: 'gold',
  faltam_itens: 'gold',
  em_andamento: 'blue',
  so_modelo: 'gold',
  so_comentario: 'gold',
  sem_comentario: 'default',
};

/**
 * Os dois módulos da máquina, e os dois motores que os executam.
 *
 * **Derivados do contrato, e não digitados à mão** (G-CONTRACT): módulo novo na
 * API vira erro de tipo aqui, e não uma etiqueta em branco na tela.
 */
export type ModuloAssistido = NonNullable<QuestionSummary['ingestionKind']>;
export type MotorAssistido = NonNullable<QuestionSummary['ingestionEngine']>;

/**
 * O nome de cada módulo, num lugar só.
 *
 * **O rótulo já foi "Cadastro Assistido" fixo, e virou mentira** quando nasceu o
 * Comentário Assistido: a tela dizia que o comentário tinha sido transcrito,
 * quando ele foi escrito. Escrito duas vezes - uma na lista, outra no histórico
 * -, o nome divergiria de novo na primeira mudança.
 */
export const MODULO_ASSISTIDO_LABEL: Record<ModuloAssistido, string> = {
  cadastro: 'Cadastro Assistido',
  comentario: 'Comentário Assistido',
};

export const MODULOS_ASSISTIDOS = Object.keys(MODULO_ASSISTIDO_LABEL) as ModuloAssistido[];

/** Quem executou: a nuvem da OpenRouter ou o Claude Code, pelo MCP. */
export const MOTOR_ASSISTIDO_LABEL: Record<MotorAssistido, string> = {
  claude_code: 'Claude Code',
  openrouter: 'nuvem',
};

/**
 * O que o run custou, quando custou (G-COST).
 *
 * **Vive aqui porque são duas telas, e não uma.** O histórico da questão mostra
 * o custo da versão; a gaveta da Legislação Comentada mostra o do artigo. Escrita
 * duas vezes, a mesma conta divergiria na primeira casa decimal que mudasse - e
 * a que fica para trás é a que ninguém abre todo dia.
 *
 * **Zero e ausência dão no mesmo aqui, e é deliberado:** um run sem medição
 * fecha com a coluna em `0`, e "US$ 0,00" na tela afirmaria que o trabalho não
 * custou nada. Nada dito é o honesto.
 *
 * Quatro casas abaixo de um centavo: um comentário custa dólares, mas um item
 * curto custa milésimos, e `US$ 0,00` seria o mesmo silêncio de novo.
 */
export const formatarCusto = (valor: string | null | undefined): string | null => {
  const numero = Number(valor);
  if (!valor || !Number.isFinite(numero) || numero <= 0) return null;
  return `US$ ${numero.toFixed(numero < 0.01 ? 4 : 2)}`;
};

/**
 * Os estados do item na lista de trabalho do Comentário Assistido (D-24).
 *
 * **Aqui, e não na tela do módulo, porque duas telas os mostram.** A lista de
 * trabalho os mostra para decidir o que fazer com o item; a lista de Questões,
 * para avisar que um motor vai escrever naquela questão. Escritos duas vezes, o
 * mesmo estado ganharia dois nomes - e o revisor não teria como saber que são a
 * mesma coisa.
 */
export const ESTADO_DA_LISTA_LABEL: Record<EstadoDoItem, string> = {
  pendente: 'Na lista',
  reivindicada: 'Em trabalho',
  /*
   * **"Esperando acervo" e não "Parada"** (D-40): o item não está travado, está
   * bloqueado por uma causa conhecida e com conserto conhecido. Chamá-lo de
   * parado mandaria o administrador procurar o que devolver, quando o que ele
   * precisa fazer é ingerir a fonte que falta.
   */
  aguardando_acervo: 'Esperando acervo',
  feita: 'Concluída',
  falhou: 'Falhou',
};

export const ESTADO_DA_LISTA_COR: Record<EstadoDoItem, string> = {
  pendente: 'default',
  reivindicada: 'processing',
  // Laranja, e não vermelho: não é falha, é trabalho à espera de insumo.
  aguardando_acervo: 'orange',
  feita: 'green',
  falhou: 'red',
};

export const STATUS_PROVA_LABEL: Record<ExamStatus, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  archived: 'Arquivada',
};

export const STATUS_QUESTAO_LABEL: Record<QuestionStatus, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  annulled: 'Anulada',
};

/** Cores de etiqueta: rascunho é neutro, publicado é o estado bom, anulado alerta. */
export const STATUS_QUESTAO = Object.keys(STATUS_QUESTAO_LABEL) as QuestionStatus[];

export const STATUS_QUESTAO_COR: Record<QuestionStatus, string> = {
  draft: 'default',
  published: 'green',
  annulled: 'red',
};

/**
 * O nome da situação na etiqueta.
 *
 * A anulada no ar se distingue da guardada (D-61): o status é o mesmo, e o que
 * muda é o aluno a ver ou não - que é justamente o que quem publica precisa
 * saber de relance.
 */
export function rotuloDaSituacao(status: QuestionStatus, noAr: boolean): string {
  return status === 'annulled' && noAr ? 'Anulada · no ar' : STATUS_QUESTAO_LABEL[status];
}

export const STATUS_PROVA = Object.keys(STATUS_PROVA_LABEL) as ExamStatus[];

export const STATUS_PROVA_COR: Record<ExamStatus, string> = {
  draft: 'default',
  published: 'green',
  archived: 'orange',
};

/**
 * As partes de uma prova discursiva. Cada uma é uma questão nossa, com
 * enunciado, espelho e resolução próprios - não campos de uma questão só.
 */
// Nome e rotulos da questao vivem no pacote comum: o app do aluno usa os
// mesmos, e dois textos para a mesma coisa divergem no primeiro ajuste.
export { nomeDaQuestao, TIPO_DISCURSIVA_LABEL, TIPOS_DISCURSIVOS } from '@titularis/ui';

/** Ordem em que as partes aparecem na prova, para sugerir a próxima. */
export const ORDEM_DOS_TIPOS: EssayKind[] = ['dissertation', 'practical_piece', 'essay_question'];

/** Os três campos da Resolução, na ordem em que o escopo os apresenta. */
export const CAMPOS_DE_COMENTARIO = ['legislation', 'jurisprudence', 'doctrine'] as const;

export type CampoDeComentario = (typeof CAMPOS_DE_COMENTARIO)[number];

export const COMENTARIO_LABEL: Record<CampoDeComentario, string> = {
  legislation: 'Legislação',
  jurisprudence: 'Jurisprudência',
  doctrine: 'Doutrina',
};

export const LETRAS = ['a', 'b', 'c', 'd', 'e'] as const;

/** Prova discursiva gera questão discursiva: o módulo decide, não um campo. */
export const ehDiscursiva = (module: ExamModule | undefined): boolean => module === 'essay';

/** Um comentário preenchido é o que o aluno vê como aba; vazio não aparece. */
export const comentariosPreenchidos = (commentary: Commentary | null | undefined): CampoDeComentario[] =>
  CAMPOS_DE_COMENTARIO.filter((campo) => Boolean(commentary?.[campo]));

const formatoDeData = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

export const formatarData = (valor: string | null | undefined): string =>
  valor ? formatoDeData.format(new Date(valor)) : '-';

/* ------------------------------------------------------------- dinheiro */

/**
 * As chaves **fixas**, pelo nome que o Róger e o Calebe usam.
 *
 * **Uma chave de acesso não é uma fase de prova** (D-20): `enac`, `objective` e
 * `essay` coincidem com os módulos porque cada fase é vendida à parte, e a
 * Legislação Comentada é a prova de que as duas dimensões não são a mesma - ela
 * é assinável sem ser fase de nada.
 *
 * **Só as fixas moram aqui, e a lista encolheu em 07/09/2026** (D-44/D-45). A
 * chave de uma Legislação Comentada nomeia uma lei pelo id, e o nome dela só
 * existe depois de uma ida ao acervo: quem manda o rótulo é a API, junto da
 * chave, e é por isso que `rotuloDaChave` devolve a chave crua quando não
 * reconhece - melhor um nome cru que um vazio.
 */
export const CHAVE_DE_ACESSO_LABEL: Record<ChaveDeAcesso, string> = {
  enac: 'Enac',
  objective: '1ª fase',
  essay: '2ª fase',
};

export const CHAVES_DE_ACESSO = Object.keys(CHAVE_DE_ACESSO_LABEL) as ChaveDeAcesso[];

/** Chave desconhecida aparece como veio: melhor um nome cru que um vazio. */
export const rotuloDaChave = (chave: string): string =>
  CHAVE_DE_ACESSO_LABEL[chave as ChaveDeAcesso] ?? chave;

/**
 * Centavos do servidor, reais na tela.
 *
 * O banco guarda inteiro em centavos porque é a forma que não perde precisão
 * entre provedores; nenhuma tela mostra centavo cru.
 */
export const emReais = (centavos: number): string =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** `card` e `pix` como se fala deles. */
export const FORMA_LABEL: Record<string, string> = {
  pix: 'Pix',
  card: 'Cartão',
};

/*
 * O `rotuloDoPlano` mora em `@titularis/ui`, com o do app.
 *
 * Ele nasceu aqui e durou uma rodada: o app escreveu a própria cópia no mesmo
 * dia, e as duas já saíram divergindo no caso geral - "De 3 meses" contra
 * "3 meses". Duas telas nomeando o mesmo plano de dois jeitos é o defeito que o
 * G-CANONICAL existe para impedir, e a correção é o pacote compartilhado, não
 * um comentário pedindo cuidado.
 */

/**
 * O que uma forma de pagamento promete, numa linha.
 *
 * A parcela é **arredondada**, e o texto diz "até": quem parcela em 12x paga
 * doze parcelas que não fecham exatas, e prometer o centavo exato aqui seria
 * prometer o que o provedor não cumpre - ele joga a diferença na última.
 */
export const condicaoDoPreco = (amountCents: number, maxInstallments: number): string =>
  maxInstallments > 1
    ? `até ${maxInstallments}x de ${emReais(Math.round(amountCents / maxInstallments))}`
    : 'à vista';

/**
 * As situações de um pedido, e o que cada uma quer dizer.
 *
 * Derivadas do contrato (G-CONTRACT): situação nova na API vira erro de tipo
 * aqui, e não uma etiqueta em branco na tela.
 */
export const STATUS_PEDIDO_LABEL: Record<OrderStatus, string> = {
  criado: 'Aguardando',
  pago: 'Pago',
  expirado: 'Expirado',
  cancelado: 'Cancelado',
  estornado: 'Estornado',
  contestado: 'Contestado',
};

export const STATUS_PEDIDO_COR: Record<OrderStatus, string> = {
  criado: 'processing',
  pago: 'green',
  expirado: 'default',
  cancelado: 'default',
  // Dinheiro que voltou: as duas tiram o acesso, e as duas merecem vermelho.
  estornado: 'red',
  contestado: 'red',
};

export const STATUS_PEDIDO = Object.keys(STATUS_PEDIDO_LABEL) as OrderStatus[];

/* --------------------------------------------------------- trilha */

/**
 * O nome de cada ação da trilha, num lugar só.
 *
 * Mora aqui, e não dentro da `TrilhaPage`, desde que a tela de Produtos passou
 * a mostrar as alterações de um produto na própria página: a mesma ação escrita
 * em dois lugares ganharia dois nomes, e quem lê não teria como saber que são a
 * mesma coisa.
 */
export const ACAO_LABEL: Record<string, string> = {
  'questao.publicou': 'Publicou questão',
  'questao.voltou_para_rascunho': 'Voltou para rascunho',
  'questao.anulou': 'Anulou questão',
  'questao.desanulou': 'Desfez a anulação',
  'questao.anulada_saiu_do_ar': 'Tirou anulada do ar',
  'questao.excluiu': 'Excluiu questão',
  'acesso.concedeu': 'Concedeu acesso',
  'acesso.revogou': 'Revogou acesso',
  'acesso.mudou_limites': 'Mudou limites',
  /*
   * As ações sobre uma conta, da seção de Pessoas. A troca de papel entrou aqui
   * em 18/09/2026: ela acontecia desde sempre pelo cliente do BetterAuth, que
   * não escreve na trilha - de modo que "quem promoveu o Róger, e quando" não
   * tinha resposta.
   */
  'conta.mudou_papel': 'Mudou o nível de acesso',
  'conta.suspendeu': 'Suspendeu conta',
  'conta.reativou': 'Reativou conta',
  'conta.encerrou_sessoes': 'Encerrou sessões',
  'conta.reenviou_confirmacao': 'Reenviou confirmação',
  'legislacao.publicou': 'Publicou legislação',
  'legislacao.excluiu': 'Excluiu legislação',
  'run.descartou_sugestoes': 'Descartou sugestões',
  'acervo.excluiu': 'Excluiu do acervo',
  'acervo.desatualizou': 'Desatualizou fonte',
  'acervo.reativou': 'Reativou fonte',
  'produto.criou': 'Criou produto',
  'produto.editou': 'Editou produto',
  'produto.excluiu': 'Excluiu produto',
  'preco.criou': 'Definiu preço',
  'preco.mudou': 'Mudou preço',
  'preco.ativou': 'Voltou a vender',
  'preco.desativou': 'Tirou de venda',
  'preco.excluiu': 'Excluiu preço',
};
